import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { Camera, Save, Trash2 } from 'lucide-react'
import { useRef } from 'react'

const inputCls = "w-full bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors text-sm"

const ClientImpostazioni = () => {
  const { profile, refreshProfile, getDisplayName, signOut } = useAuth()
  const [fullName, setFullName] = useState(profile?.full_name || '')
  const [nickname, setNickname] = useState(profile?.nickname || '')
  const [azienda, setAzienda] = useState(profile?.azienda || '')
  const [loading, setLoading] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const fileInputRef = useRef(null)

  const avatarUrl = profile?.avatar_url ? `${profile.avatar_url}?t=${Date.now()}` : null

  const handleSave = async () => {
    setLoading(true); setError(null); setMessage(null)
    const { error } = await supabase.from('profiles').update({
      full_name: fullName,
      nickname,
      azienda: azienda || null,
    }).eq('id', profile.id)
    if (error) setError(error.message)
    else { await refreshProfile(); setMessage('Profilo aggiornato.') }
    setLoading(false)
  }

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploadingAvatar(true); setError(null)
    const ext = file.name.split('.').pop()
    const path = `${profile.id}/avatar.${ext}`
    const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true })
    if (uploadError) { setError(uploadError.message); setUploadingAvatar(false); return }
    const { data } = supabase.storage.from('avatars').getPublicUrl(path)
    const { error: updateError } = await supabase.from('profiles').update({ avatar_url: data.publicUrl }).eq('id', profile.id)
    if (updateError) setError(updateError.message)
    else { await refreshProfile(); setMessage('Foto profilo aggiornata.') }
    setUploadingAvatar(false)
  }

  const handleDeleteAccount = async () => {
    setDeleteLoading(true)
    const { error } = await supabase.rpc('delete_my_account')
    if (error) { setError(error.message); setDeleteLoading(false); return }
    await signOut()
  }

  return (
    <div className="max-w-lg">
      <h1 className="text-2xl font-bold text-white mb-8">Impostazioni profilo</h1>

      {/* Avatar */}
      <div className="flex items-center gap-6 mb-8">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-violet-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
            {avatarUrl
              ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
              : <span>{getDisplayName()?.[0]?.toUpperCase() ?? '?'}</span>}
          </div>
          <button onClick={() => fileInputRef.current.click()} disabled={uploadingAvatar}
            className="absolute -bottom-1 -right-1 w-7 h-7 bg-violet-600 hover:bg-violet-500 rounded-full flex items-center justify-center transition-colors">
            <Camera size={13} className="text-white" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
        </div>
        <div>
          <p className="text-white font-medium">{getDisplayName()}</p>
          <p className="text-gray-500 text-sm">{profile?.azienda || 'Cliente'}</p>
          {uploadingAvatar && <p className="text-violet-400 text-xs mt-1">Caricamento...</p>}
        </div>
      </div>

      <div className="space-y-5">
        <div className="space-y-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Profilo</p>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome completo</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Nickname</label>
            <input type="text" value={nickname} onChange={e => setNickname(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Azienda / Brand <span className="text-gray-600">(opzionale)</span></label>
            <input type="text" value={azienda} onChange={e => setAzienda(e.target.value)} placeholder="es. Acme S.r.l." className={inputCls} />
          </div>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}
        {message && <p className="text-green-400 text-sm">{message}</p>}

        <button onClick={handleSave} disabled={loading}
          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-lg transition-colors">
          <Save size={16} />
          {loading ? 'Salvataggio...' : 'Salva modifiche'}
        </button>

        {/* Zona pericolosa */}
        <div className="pt-6 mt-6 border-t border-gray-800 space-y-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Zona pericolosa</p>
          {!showDeleteConfirm ? (
            <button onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 px-4 py-2.5 rounded-lg transition-colors">
              <Trash2 size={15} />
              Elimina account
            </button>
          ) : (
            <div className="bg-red-950/30 border border-red-900/50 rounded-xl p-4 space-y-3">
              <p className="text-red-300 text-sm font-medium">Sei sicuro di voler eliminare il tuo account?</p>
              <p className="text-red-400/70 text-xs">Questa azione è irreversibile. Tutti i tuoi dati verranno cancellati permanentemente.</p>
              <div className="flex gap-3">
                <button onClick={handleDeleteAccount} disabled={deleteLoading}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                  <Trash2 size={14} />
                  {deleteLoading ? 'Eliminazione...' : 'Sì, elimina'}
                </button>
                <button onClick={() => setShowDeleteConfirm(false)}
                  className="text-sm text-gray-400 hover:text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ClientImpostazioni
